import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import SmartForm, { Field } from "@insightfulscience/smart-react/SmartForm";
import { useLocale } from "@insightfulscience/smart-react/i18";
import React, { useCallback, useMemo, useState } from "react";
import Spinner from "@insightfulscience/atomic-react/Spinner";
import Select from "@insightfulscience/atomic-react/Select";
import Input from "@insightfulscience/atomic-react/Input";
import { compose } from "@insightfulscience/shared-utils/fn";
import Textarea from "@insightfulscience/atomic-react/Textarea";
import Button from "@insightfulscience/atomic-react/Button";
import { useFormActionWithAnyCaptcha } from "@insightfulscience/smart-react/anyCaptcha";
import { sendContactForm } from "./action";
import { initialState, productOfInterest, validateForm } from "./form";
import { useCountries, useCountryStates } from "../countries";
import { publicConfig } from "../config";
import FlashErrorMessage from "../utils/FlashErrorMessage";

const eventToNumber = ({ target }) => +target.value;

const ContactForm = () => {
	const [t] = useLocale("contact-form");

	const [countryID, setCountryID] = useState(initialState.countryID);

	const handleCountryChange = useMemo(
		() => compose(setCountryID, eventToNumber), // callback
		[setCountryID],
	);
	const [countries, countryById] = useCountries();
	const states = useCountryStates(countryID);

	const setCountry = useCallback(
		(form, chosenCountry) => ({
			...form,
			countryID: chosenCountry,
			address1: countryById.get(chosenCountry)?.name || "",
			state: "",
		}),
		[],
	);

	const [action, { isLoading, error, reset }] = useFormActionWithAnyCaptcha(sendContactForm, {
		endOn: "then",
		googleRecaptchaSiteKey: publicConfig.GOOGLE_RECAPTCHA_SITE_KEY,
		castlePublicKey: publicConfig.CASTLE_PUBLIC_KEY,
		action: "sendContactForm",
	});

	return (
		<>
			{isLoading && <Spinner />}
			{error && <FlashErrorMessage text={t(error)} />}
			<SmartForm
				{...{ action, validateForm, initialState }}
				onTouched={reset}
				textRender={t}
				data-qa-selector="contact-form"
				id="contact-form"
			>
				<Row px utility={{ justify: "center" }}>
					<Col lg={6}>
						<Field
							label="FIRST_NAME"
							id="firstName"
							fieldClassName="mb-6"
							name="firstName"
							required
							maxLength={50}
							data-qa-selector="firstName-field"
						/>
					</Col>
					<Col lg={6}>
						<Field
							label="LAST_NAME"
							id="lastName"
							fieldClassName="mb-6"
							name="lastName"
							required
							maxLength={50}
							data-qa-selector="lastName-field"
						/>
					</Col>
				</Row>
				<Row px utility={{ justify: "center" }}>
					<Col lg={6}>
						<Field
							label="EMAIL"
							id="email"
							fieldClassName="mb-6"
							name="email"
							required
							maxLength={100}
							data-qa-selector="email-field"
						/>
					</Col>
					<Col lg={6}>
						<Field
							label="COMPANY"
							id="company"
							fieldClassName="mb-6"
							name="company"
							required
							maxLength={50}
							data-qa-selector="company-field"
						/>
					</Col>
				</Row>
				<Row px utility={{ justify: "center" }}>
					<Col lg={6}>
						<Field
							fieldClassName="mb-6"
							name="countryID"
							label="COUNTRY"
							required
							Control={Select}
							parseEvent={eventToNumber}
							onChange={handleCountryChange}
							setState={setCountry}
							data-qa-selector="country-select-field"
						>
							<>
								<Select.Placeholder>{t("COUNTRY_PLACEHOLDER")}</Select.Placeholder>
								{Select.renderOptions(
									countries,
									({ id }) => `${id}`,
									({ name }) => `${name}`,
								)}
							</>
						</Field>
					</Col>
					<Col lg={6}>
						<Field
							fieldClassName="mb-6"
							name="state"
							label="STATE"
							Control={states ? Select : Input}
							required
							data-qa-selector="state-field"
						>
							{states && (
								<>
									<Select.Placeholder>{t("STATE_PLACEHOLDER")}</Select.Placeholder>
									{Select.renderOptions(
										states,
										({ isoAlpha2 }) => isoAlpha2,
										({ isoName }) => isoName,
									)}
								</>
							)}
						</Field>
					</Col>
				</Row>
				<Row px utility={{ justify: "center" }}>
					<Col>
						<Field
							fieldClassName="mb-6"
							name="productOfInterest"
							label="PRODUCT_OF_INTEREST"
							required
							Control={Select}
							data-qa-selector="product-of-interest-select-field"
						>
							<Select.Placeholder>{t("PRODUCT_OF_INTEREST_PLACEHOLDER")}</Select.Placeholder>
							{Select.renderOptions(
								productOfInterest,
								({ value }) => value,
								({ text }) => t(text),
							)}
						</Field>
					</Col>
				</Row>
				<Row px utility={{ justify: "center" }}>
					<Col lg={12} md={12} sm={12} className="py-0">
						<Field
							id="message"
							fieldClassName="mb-6"
							Control={Textarea}
							name="message"
							label="MESSAGE"
							required
							maxLength={400}
							data-qa-selector="message-field"
						/>

						<Button
							type="submit"
							styleType="primary"
							disabled={isLoading}
							data-qa-selector="contact-form-submit-btn"
						>
							{t("SUBMIT")}
						</Button>
					</Col>
				</Row>
			</SmartForm>
		</>
	);
};

export default ContactForm;
